import { useControllerContext } from "../../../hooks/controller-context";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { pubState } from "../../../../../recoil/pub.atom";
import {
  EntryType,
  getTournamentUser,
  getTournamentUserIsSitOut,
  participantDeleteTournament,
  reBuyInOrAddOnDeleteTournament,
  reservationParticipation,
  TournamentParticipationDto,
} from "../../../../../api/tournament-participant";
import { useEffect, useState } from "react";
import {
  CurrentReservationType,
  getCurrentReservationByTournamentId,
  ReservationTournamentResponse,
} from "../../../../../api/reservation";
import {
  getMemberSearch,
  getPubMemberList,
  joinMemberApi,
  PubMemberDto,
} from "../../../../../api/member";
import { memberSitOut } from "../../../../../api/tournament";
import { customDialogState } from "../../../../../recoil/custom-dialog.atom";
import { dialogState } from "../../../../../recoil/dialog.atom";
import { isRegistrationAvailable } from "../../../../../utils/timer";
import { useParams } from "react-router-dom";
import { isTournamentAccess } from "../../../../../utils/owner";

export enum SitInTab {
  SIT_IN = "SIT_IN",
  SIT_OUT = "SIT_OUT",
}

export enum BuyInType {
  NORMAL = "NORMAL",
  RESERVATION = "RESERVATION",
  QR = "QR",
  QR_RESERVATION = "QR_RESERVATION",
}

export const useTournamentParticipant = () => {
  const {
    data,
    getData,
    level,
    setIsPopup,
    qrUserInfo,
    setQrCode,
    setQrUserInfo,
  } = useControllerContext();
  const pub = useRecoilValue(pubState);
  const limit = 20;
  const setCustomDialog = useSetRecoilState(customDialogState);
  const setDialog = useSetRecoilState(dialogState);
  const { id } = useParams();
  const tournamentId = id?.replace(/[^0-9]/g, "");
  // 탭
  const [tab, setTab] = useState<SitInTab>(SitInTab.SIT_IN);
  // 스크롤 페이징
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasMoreData, setHasMoreData] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState<number>(1);
  // 싯인 정렬
  const [sort, setSort] = useState<string>("name");
  // 닉네임 검색
  const [searchNickname, setSearchNickname] = useState<string>("");
  // 디바운스된 검색어 (성능 개선)
  const [debouncedSearch, setDebouncedSearch] = useState<string>("");
  // 싯인 관련 변수
  const [sitInCount, setSitInCount] = useState<number>(0);
  const [sitOutCount, setSitOutCount] = useState<number>(0);
  const [participantList, setParticipantList] = useState<
    TournamentParticipationDto[]
  >([]);
  // 예약 관련 변수
  const [notSitInReservationCount, setNotSitInReservationCount] =
    useState<number>(0);
  const [reservationList, setReservationList] = useState<
    ReservationTournamentResponse[]
  >([]);
  // 참가할 멤버 (참가, 예약 참가)
  const [participantMember, setParticipantMember] =
    useState<PubMemberDto | null>(null);
  // 참가한 멤버
  const [selectedMember, setSelectedMember] =
    useState<TournamentParticipationDto | null>(null);
  // 팝업 상태
  const [isReservationPopup, setIsReservationPopup] = useState(false);
  const [isBuyInPopup, setIsBuyInPopup] = useState(false);
  const [isHistoryPopup, setIsHistoryPopup] = useState(false);
  const [isReEntryPopup, setIsReEntryPopup] = useState(false);
  const [isQrReEntryPopup, setIsQrReEntryPopup] = useState(false);
  const [isPrizePopup, setIsPrizePopup] = useState(false);
  // 애드온 엔트리 추가 여부
  const [isCount, setIsCount] = useState(false);
  const [reEntryType, setReEntryType] = useState<EntryType>(EntryType.RE_BUY);
  const [buyInType, setBuyInType] = useState<BuyInType>(BuyInType.NORMAL);
  const isReEntryType = (tab: EntryType) => reEntryType === tab;
  const isBuyInType = (tab: BuyInType) => buyInType === tab;

  useEffect(() => {
    handleQrUserLogic();
  }, [qrUserInfo]);

  // QR 로직
  const handleQrUserLogic = () => {
    if (!qrUserInfo) return;

    // 회원 아닐 시 회원 추가 후 참가 로직 진행
    if (!qrUserInfo?.isMember) {
      handleQrUserJoin();
      setQrCode("");
      setQrUserInfo(null);
      return;
    }

    if (qrUserInfo.isParticipant) {
      // 리엔트리 / 애드온 참가 팝업
      handleQrReEntry();
    } else {
      // 참가 로직
      handleQrParticipant();
    }

    setQrCode("");
    setQrUserInfo(null);
  };

  // 회원 추가
  const handleQrUserJoin = async () => {
    if (!qrUserInfo || !pub || !tournamentId) return;
    if (!isTournamentAccess(+tournamentId, pub.tournamentIds)) {
      alert(`토너먼트 ID ${tournamentId}에 대한 권한이 없습니다. `);
      return;
    }

    const phoneNumber = qrUserInfo.phoneNumber.slice(-4);

    try {
      // 회원 검색
      const res = await getMemberSearch(
        qrUserInfo.nickname,
        phoneNumber,
        pub.id,
      );

      if (!res.payload.alreadyMember) {
        alert("러너펍 첫 방문 회원입니다. ");
      }

      // 회원 가입
      if (!res.payload.isPubMember) {
        const req = {
          nickname: qrUserInfo.nickname,
          phoneNumber,
          pubId: pub.id,
        };
        await joinMemberApi(req);
      }
    } catch (e: any) {
      console.error(e);
      if (e.status === 403) {
        alert(e.response.data.message);
        return;
      }
      alert(e.message);
    } finally {
      // 참가 로직
      handleQrParticipant();
    }
  };

  // QR 참가
  const handleQrParticipant = async () => {
    if (!qrUserInfo || !pub || !data) return;
    // 회원 정보 조회 (qrUserInfo에서 member 정보 없을 때 대비)
    const res = await getPubMemberList(qrUserInfo.nickname, pub.id);
    const selectMember = res.memberList.find(
      (item) => item.nickname === qrUserInfo.nickname,
    );
    if (!selectMember) return;

    if (!qrUserInfo.isReservation) {
      // 일반 참가
      setBuyInType(BuyInType.QR);
    } else {
      // 예약 참가
      const current = qrUserInfo.currentReservation;
      if (current && current?.keepSeed) {
        // 시드 예약 참가
        const request = {
          currentReservationId: current.id,
          memberId: selectMember.memberId,
          pubId: pub.id,
          tournamentId: data.id,
          level: level,
          description: "",
        };
        await reservationParticipation(request);
        alert(`'${qrUserInfo.nickname}'님이 참가되었습니다. `);
        reloadData();
        return;
      }
      // 일반 예약 참가
      setBuyInType(BuyInType.QR_RESERVATION);
    }
    // 참가 팝업
    setParticipantMember(selectMember);
    setIsBuyInPopup(true);
  };

  // QR 리엔트리 / 애드온
  const handleQrReEntry = async () => {
    if (!data || !qrUserInfo) return;
    // 참가 데이터 조회
    const participation = await getTournamentUser(
      data.id,
      1,
      10,
      qrUserInfo.nickname,
    );
    const member = participation.list[0];
    if (!member) return;
    // 리엔트리 / 애드온 팝업
    setSelectedMember(member);
    setIsQrReEntryPopup(true);
  };

  // 팝업 상태
  useEffect(() => {
    if (
      isReservationPopup ||
      isBuyInPopup ||
      isHistoryPopup ||
      isReEntryPopup ||
      isPrizePopup ||
      isQrReEntryPopup
    ) {
      setIsPopup(true);
      return;
    }
    setIsPopup(false);
  }, [
    setIsPopup,
    isReservationPopup,
    isBuyInPopup,
    isHistoryPopup,
    isReEntryPopup,
    isPrizePopup,
    isQrReEntryPopup,
  ]);

  // 검색어 디바운스 처리
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchNickname);
    }, 200); // 200ms 디바운스

    return () => clearTimeout(timer);
  }, [searchNickname]);

  useEffect(() => {
    setCurrentPage(1);
    setHasMoreData(true);
    getParticipantList();
  }, [debouncedSearch, tab, sort]);

  // 동작 실행 후 데이터 업데이트
  const reloadData = () => {
    setCurrentPage(1);
    setHasMoreData(true);
    getReservationData();
    getParticipantList();
    getData();
  };

  useEffect(() => {
    reloadData();
  }, []);

  /////////////////////////////////// 싯인 | 싯아웃 정보 조회 ///////////////////////////////////
  const getParticipantList = async (isReset: boolean = true) => {
    if (isLoading || !tournamentId) return;

    setIsLoading(true);
    try {
      const pageToFetch = isReset ? 1 : currentPage;
      const res = await getTournamentUserIsSitOut(
        +tournamentId,
        tab === SitInTab.SIT_IN ? false : true,
        pageToFetch,
        limit,
        debouncedSearch,
        sort,
      );

      setSitInCount(res.totalSitIn);
      setSitOutCount(res?.totalSitOut);
      // 페이징된 데이터 추가 또는 초기화
      setParticipantList((prev) =>
        isReset ? res.list : [...prev, ...res.list],
      );

      // 더 이상 데이터가 없는지 확인
      setHasMoreData(pageToFetch < Math.ceil(res.total / limit));
    } catch (error) {
      setParticipantList([]);
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  // 검색
  const onSearchClick = () => {
    setCurrentPage(1);
    setHasMoreData(true);
    getParticipantList();
  };

  // 스크롤 페이징
  useEffect(() => {
    if (currentPage > 1) {
      getParticipantList(false);
    }
  }, [currentPage]);

  ///////////////////////////////////// 예약자 정보 조회 /////////////////////////////////////
  const getReservationData = async () => {
    if (!tournamentId) return;
    try {
      const res = await getCurrentReservationByTournamentId(+tournamentId);
      const sortedRes = res.payload.sort((a, b) => {
        if (a.isSitin === false && b.isSitin !== false) {
          return -1;
        } else if (a.isSitin !== false && b.isSitin === false) {
          return 1;
        } else {
          return 0;
        }
      });
      setReservationList(sortedRes);
      const filterList = res.payload.filter((item) => !item.isSitin);
      setNotSitInReservationCount(filterList.length);
    } catch {
      setReservationList([]);
    }
  };

  ///////////////////////////////////// 예약자 싯인 /////////////////////////////////////
  const handleReservationParticipation = async (
    reservation: ReservationTournamentResponse,
  ) => {
    if (!pub || !data) return;

    const isRegistration = isRegistrationAvailable(data);
    if (!isRegistration) {
      alert("레지마감되었습니다. ");
      return;
    }

    try {
      if (reservation.type !== CurrentReservationType.NORMAL) {
        // 일반 예약 아닐 시 api 참가
        const request = {
          currentReservationId: reservation.id,
          memberId: reservation.memberId,
          pubId: pub.id,
          tournamentId: data.id,
          level: level,
          description: "",
        };
        await reservationParticipation(request);
        alert(`'${reservation.nickname}'님이 참가되었습니다. `);
        reloadData();
        return;
      }

      // 일반 예약일 시 참가 결제 팝업
      // 펍 회원 여부 확인
      const res = await getPubMemberList(reservation.nickname, pub.id);
      const selectMember = res.memberList.find(
        (item) => item.memberId === reservation.memberId,
      );
      if (!selectMember) {
        alert("본사에 문의해주세요.");
        return;
      }
      setBuyInType(BuyInType.RESERVATION);
      setParticipantMember(selectMember);
      setIsBuyInPopup(true);
      setIsReservationPopup(false);
      reloadData();
    } catch (error: any) {
      console.error(error);
      if (error.status === 403) {
        alert(error.response.data.message);
        return;
      }
    }
  };

  //////////////////////////////////////// 싯인 관련 핸들러 ///////////////////////////////////////

  // 레지마감 팝업
  const handleDialog = () => {
    setDialog({
      title: "레지마감되었습니다. ",
      isCancel: false,
      confirm: () => {
        setDialog(null);
      },
    });
  };

  // 싯인 추가
  const handleBuyIn = () => {
    // 레지마감 여부
    if (!data) return;

    if (!isTournamentAccess(data.id, pub?.tournamentIds)) {
      alert(`토너먼트 ID ${data.id}에 대한 권한이 없습니다. `);
      return;
    }

    const isRegistration = isRegistrationAvailable(data);
    if (!isRegistration) {
      handleDialog();
      return;
    }

    setBuyInType(BuyInType.NORMAL);
    setParticipantMember(null);
    setIsBuyInPopup(true);
  };

  // 참가자 싯인 / 싯아웃
  const handleSitOut = async (
    member: TournamentParticipationDto,
    isSitOut: boolean,
  ) => {
    if (!data) return;
    if (!isTournamentAccess(data.id, pub?.tournamentIds)) {
      alert(`토너먼트 ID ${data.id}에 대한 권한이 없습니다. `);
      return;
    }
    const confirmationMessage = isSitOut
      ? "싯아웃 하시겠습니까?"
      : "싯인으로 되돌리시겠습니까?";
    if (window.confirm(confirmationMessage)) {
      try {
        await memberSitOut(member.id, isSitOut, data.id);
        reloadData();
        alert("처리되었습니다.");
      } catch (e: any) {
        if (e.status === 403) {
          alert(e.response.data.message);
          return;
        }
        alert(e.message);
      }
    }
  };

  // 유저 이용기록
  const handleOpenHistory = (item: TournamentParticipationDto) => {
    setSelectedMember(item);
    setIsHistoryPopup(true);
  };

  // 리바인 / 애드온 추가
  const handleReEntry = (item: TournamentParticipationDto, type: EntryType) => {
    // 레지마감 여부
    if (!data) return;
    if (!isTournamentAccess(data.id, pub?.tournamentIds)) {
      alert(`토너먼트 ID ${data.id}에 대한 권한이 없습니다. `);
      return;
    }
    const isRegistration = isRegistrationAvailable(data);
    if (!isRegistration) {
      handleDialog();
      return;
    }

    setSelectedMember(item);
    setReEntryType(type);
    if (type === EntryType.RE_BUY) setIsReEntryPopup(true);

    if (type === EntryType.ADD_ON)
      setCustomDialog({
        title: `애드온을 전체 엔트리에 \n포함 하시겠습니까?`,
        confirm: () => {
          setIsCount(true);
          setIsReEntryPopup(true);
        },
        confirmText: "예",
        cancelText: "아니오",
        cancel: () => {
          setIsCount(false);
          setIsReEntryPopup(true);
          setCustomDialog(null);
        },
      });
  };

  // 참가자 삭제
  const handleParticipantDelete = async (memberId: number) => {
    if (!data) return;
    if (!isTournamentAccess(data.id, pub?.tournamentIds)) {
      alert(`토너먼트 ID ${data.id}에 대한 권한이 없습니다. `);
      return;
    }
    if (
      window.confirm(
        "삭제하게되면 해당유저의 엔트리/리엔트리/애드온 데이터가 초기화되며 싯인에서 삭제됩니다. \n삭제하시겠습니까? ",
      )
    ) {
      try {
        const res = await participantDeleteTournament(memberId, data.id);
        reloadData();
        alert(res.message);
      } catch (e: any) {
        if (e.status === 403) {
          alert(e.response.data.message);
          return;
        }
        alert(e.message);
      }
    }
    return;
  };

  // 리엔트리, 애드온 삭제
  const handleReEntryDelete = async (id: number) => {
    if (data?.isEnd) {
      alert("토너먼트 종료 후에는 삭제할 수 없습니다. ");
      return;
    }
    if (!window.confirm("삭제하시겠습니까?")) return;

    if (data && !isTournamentAccess(data?.id, pub?.tournamentIds)) {
      alert(`토너먼트 ID ${data?.id}에 대한 권한이 없습니다. `);
      return;
    }

    try {
      await reBuyInOrAddOnDeleteTournament(id);
      alert("해당 내역이 삭제되었습니다. ");
      reloadData();
    } catch (e: any) {
      if (e.status === 403) {
        alert(e.response.data.message);
        return;
      }
      alert(e.message);
    }
  };

  return {
    // 싯인 / 싯아웃 탭
    tab,
    setTab,
    // 리엔트리 타입
    reEntryType,
    setReEntryType,
    isReEntryType,
    // 바이인 타입
    buyInType,
    setBuyInType,
    isBuyInType,
    // 유저 정렬
    sort,
    setSort,
    // 스크롤 페이징
    isLoading,
    setIsLoading,
    hasMoreData,
    setHasMoreData,
    currentPage,
    setCurrentPage,
    // 닉네임 검색
    searchNickname,
    setSearchNickname,
    onSearchClick,
    // 데이터
    participantList,
    sitInCount,
    sitOutCount,
    reservationList,
    notSitInReservationCount,
    // 핸들러
    reloadData,
    handleReservationParticipation,
    handleBuyIn,
    handleSitOut,
    handleOpenHistory,
    handleReEntry,
    handleParticipantDelete,
    handleReEntryDelete,
    // 참가할 멤버
    participantMember,
    setParticipantMember,
    // 참가한 멤버
    selectedMember,
    setSelectedMember,
    // 팝업 상태
    isBuyInPopup,
    setIsBuyInPopup,
    isReservationPopup,
    setIsReservationPopup,
    isHistoryPopup,
    setIsHistoryPopup,
    isReEntryPopup,
    setIsReEntryPopup,
    isPrizePopup,
    setIsPrizePopup,
    isQrReEntryPopup,
    setIsQrReEntryPopup,
    // 애드온 엔트리 추가 여부
    isCount,
    setIsCount,
  };
};
